import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

const SLIDE_WIDTHS = [300, 450, 600, 750, 900, 1200, 1500, 1800];
const THUMBNAIL_WIDTHS = [300, 375, 450, 525, 600, 675, 750, 768, 850, 900, 1024];
const MAX_IDLE_PRELOADS = 8;

/** Image URLs already requested, kept at module level so a replaced gallery does not preload them again. */
const preloadedImages = new Set();

/**
 * Returns the same Shopify CDN image URL at a different width.
 * @param {string} url
 * @param {number} width
 * @returns {string}
 */
function withWidth(url, width) {
  const resized = new URL(url, window.location.href);
  resized.searchParams.set('width', String(width));
  return resized.toString();
}

/**
 * @param {string} url
 * @param {number[]} widths
 * @returns {string}
 */
function buildSrcset(url, widths) {
  return widths.map((width) => `${withWidth(url, width)} ${width}w`).join(', ');
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Warm the cache before the click. Hover/focus covers desktop, touchstart covers mobile.
    for (const type of ['pointerover', 'focusin', 'touchstart']) {
      target?.addEventListener(type, this.#handleSwatchIntent, { signal, passive: true });
    }

    this.#preloadWhenIdle(target);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Preloads a variant image using the same srcset and sizes as the gallery image, so the browser
   * picks the same candidate file and the later swap is served from cache.
   *
   * @param {HTMLElement | undefined} input - A variant option input carrying data-variant-image.
   */
  #preloadVariantImage(input) {
    const url = input?.dataset.variantImage;
    if (!url || preloadedImages.has(url)) return;

    preloadedImages.add(url);

    const image = new Image();
    image.sizes = this.querySelector('.product-media__image')?.sizes ?? '';
    image.fetchPriority = 'low';
    image.srcset = buildSrcset(url, SLIDE_WIDTHS);
    image.src = url;
  }

  /**
   * @param {Event} event
   */
  #handleSwatchIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const label = event.target.closest('.variant-option__button-label');
    const input = label?.querySelector('input[data-variant-image]') ?? event.target.closest('input[data-variant-image]');

    if (input instanceof HTMLElement) this.#preloadVariantImage(input);
  };

  /**
   * Preloads the other variant images once the page is idle, capped to protect the initial load.
   *
   * @param {Element | null | undefined} target - The closest section or dialog.
   */
  #preloadWhenIdle(target) {
    const run = () => {
      const inputs = Array.from(target?.querySelectorAll('variant-picker input[data-variant-image]:not(:checked)') ?? []);

      for (const input of inputs.slice(0, MAX_IDLE_PRELOADS)) {
        if (input instanceof HTMLElement) this.#preloadVariantImage(input);
      }
    };

    const schedule = () => ('requestIdleCallback' in window ? requestIdleCallback(run, { timeout: 4000 }) : setTimeout(run, 2000));

    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true, signal: this.#controller.signal });
  }

  /**
   * Swaps the first slide and its thumbnail to the selected variant's image right away, without
   * waiting for the section fetch. The variant update event still replaces the whole gallery afterwards.
   *
   * @param {Event} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    const picker = event.target instanceof Element ? event.target.closest('variant-picker') : null;
    const input = picker?.querySelector('input[data-variant-image]:checked');
    const url = input instanceof HTMLElement ? input.dataset.variantImage : undefined;
    if (!(input instanceof HTMLElement) || !url) return;

    const image = this.querySelector('slideshow-slide .product-media__image');
    const wrapper = image?.closest('.product-media');
    if (!(image instanceof HTMLImageElement) || !(wrapper instanceof HTMLElement)) return;
    if (wrapper.dataset.mediaId === input.dataset.variantMediaId) return;

    // Slideshow.select() ignores options and uses smooth scrollIntoView, which animates the slide
    // and also scrolls the page on mobile. Reset only the slide track, instantly.
    const track = this.slideshow?.querySelector('slideshow-slides');
    if (track instanceof HTMLElement) track.scrollTo({ left: 0, behavior: 'instant' });

    const highRes = withWidth(url, 3840);
    image.srcset = buildSrcset(url, SLIDE_WIDTHS);
    image.src = url;
    image.dataset.maxResolution = highRes;

    if (wrapper instanceof HTMLAnchorElement) wrapper.href = highRes;
    wrapper.dataset.mediaId = input.dataset.variantMediaId ?? '';
    if (input.dataset.variantImageRatio) wrapper.style.setProperty('--ratio', input.dataset.variantImageRatio);

    const thumbnail = this.querySelector('.slideshow-controls__thumbnails img');
    if (thumbnail instanceof HTMLImageElement) {
      thumbnail.srcset = buildSrcset(url, THUMBNAIL_WIDTHS);
      thumbnail.src = withWidth(url, 1024);
    }
  };

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
